import { useEffect, useId, useState } from "react";
import type { EstimatedYieldResponse, YieldPeriod } from "../types";

/**
 * The nine buckets the route accepts. The default is 24h and it is deliberately NOT the catalog's
 * `yieldPeriod` preference, whose default is 365d.
 */
// eslint-disable-next-line react-refresh/only-export-components -- The row owns its period list and its default.
export const ESTIMATED_YIELD_PERIODS: readonly YieldPeriod[] = [
  "15m",
  "30m",
  "1h",
  "4h",
  "8h",
  "24h",
  "7d",
  "30d",
  "365d",
];
export const DEFAULT_ESTIMATED_YIELD_PERIOD: YieldPeriod = "24h";

/**
 * A transport or HTTP failure carries no server reason, and a stale number would present an old
 * range's return as the current one. The row still never shows a blank, a dash alone or 0%.
 */
const FAILED_REASON = "estimated yield is unavailable";

export interface EstimatedYieldRowProps {
  period: YieldPeriod;
  onPeriodChange: (period: YieldPeriod) => void;
  /** The last settled response; null before the first one and after a failed request. */
  estimate: EstimatedYieldResponse | null;
  /** A request is in flight: the debounce has already settled. */
  pending: boolean;
  /** The last request failed at the transport or HTTP level. */
  failed: boolean;
}

/**
 * One informational row between Range and Amount: what the pool's fees would have paid on the
 * chosen range over the chosen period. A period return, never an annual rate.
 *
 * The browser performs NO arithmetic on the figure: the server sends `display`, the exact text to
 * print before the "%" sign, and this row prints it. No rounding, no formatting, no threshold
 * comparison. It never blocks Review position and never gates the form.
 */
export function EstimatedYieldRow({
  period,
  onPeriodChange,
  estimate,
  pending,
  failed,
}: EstimatedYieldRowProps) {
  const [tipShown, setTipShown] = useState(false);
  const tipId = useId();
  useEffect(() => {
    if (!tipShown) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTipShown(false);
    };
    window.addEventListener("keydown", dismiss);
    return () => {
      window.removeEventListener("keydown", dismiss);
    };
  }, [tipShown]);

  const settled = failed ? null : estimate;
  const value = settled !== null && settled.status === "available" ? settled.display : null;
  const reason =
    value !== null
      ? null
      : settled === null
        ? failed
          ? FAILED_REASON
          : null
        : // An "available" response without a display is as unusable as an unavailable one.
          (settled.reason ?? FAILED_REASON);
  const recalculating = pending && value !== null;
  const qualifier = recalculating
    ? "previous range"
    : value !== null && settled?.forAnyAmount === true
      ? "for any amount"
      : null;

  const extrapolatedFromDays = estimate?.extrapolatedFromDays ?? null;
  const tooltip = `Assumes the price stays inside your range, the fee flow of the last ${period} repeats, and pool liquidity does not change. Not a promise, not an annual rate.${
    extrapolatedFromDays === null
      ? ""
      : ` The ${String(extrapolatedFromDays)}-day fee flow is stretched to a year; no year of fees was measured.`
  }`;

  return (
    <section className="estimated-yield-row" aria-label="Estimated yield">
      <span className="estimated-yield-label">Estimated yield</span>
      <button
        type="button"
        className="estimated-yield-info"
        aria-label="About the estimated yield"
        aria-describedby={tipShown ? tipId : undefined}
        onFocus={() => {
          setTipShown(true);
        }}
        onBlur={() => {
          setTipShown(false);
        }}
        onPointerEnter={() => {
          setTipShown(true);
        }}
        onPointerLeave={() => {
          setTipShown(false);
        }}
      >
        <svg viewBox="0 0 14 14" aria-hidden="true">
          <circle cx="7" cy="7" r="5.6" />
          <path d="M7 6.3v3.4" strokeLinecap="round" />
          <circle className="estimated-yield-dot" cx="7" cy="4.4" r="0.5" />
        </svg>
      </button>
      <select
        className="estimated-yield-period num"
        aria-label="Estimated yield period"
        value={period}
        onChange={(event) => {
          onPeriodChange(event.target.value as YieldPeriod);
        }}
      >
        {ESTIMATED_YIELD_PERIODS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span className="estimated-yield-figure">
        {qualifier !== null && <span className="estimated-yield-qualifier num">{qualifier}</span>}
        {pending && <span className="estimated-yield-spinner" aria-hidden="true" />}
        {/* Only the settled figure lives in the live region: the spinner and the qualifier are
            outside it, so nothing is announced until the debounced request answers. */}
        <span className="estimated-yield-slot" aria-live="polite">
          {value !== null ? (
            <span
              // Remounting on a new value restarts the 150ms change animation.
              key={value}
              className={`num estimated-yield-value ${recalculating ? "stale" : ""}`}
            >
              {`${value}%`}
            </span>
          ) : reason !== null ? (
            <span className="estimated-yield-reason" title={reason}>
              {reason}
            </span>
          ) : (
            <span className="estimated-yield-skeleton" aria-hidden="true" />
          )}
        </span>
      </span>
      {tipShown && (
        <span role="tooltip" id={tipId} className="estimated-yield-tooltip">
          {tooltip}
        </span>
      )}
    </section>
  );
}
